import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import Layout from "../components/Layout";
import { useLang } from "../utils/LangContext";
import {
  areMobileRequirementsMet,
  checkMobileRequirements,
  clearMobileRequirementsPassed,
  createInitialMobileRequirements,
  isMobileRequirementsBypassedForEmail,
  markMobileRequirementsPassed,
} from "../utils/mobileRequirements";
import localStorageHelper from "../utils/localStorageHelper";

const MobileRequirements = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/game";

  const [checks, setChecks] = useState(createInitialMobileRequirements);
  const [isChecking, setIsChecking] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [showGpsHelp, setShowGpsHelp] = useState(false);
  const team = localStorageHelper.getItem("team");
  const game = localStorageHelper.getItem("game");
  const canStartGame = Boolean(game?.hash && team?.team_code);
  const isContinuingGame = canStartGame && searchParams.get("mode") !== "start";
  const startGameButtonText = isContinuingGame
    ? t("Continue playing")
    : t("Start Game");
  const requirementsBypassed = isMobileRequirementsBypassedForEmail(
    team?.team_member?.email
  );

  const requirementsMet = useMemo(
    () => requirementsBypassed || areMobileRequirementsMet(checks),
    [checks, requirementsBypassed]
  );

  const requirementRows = useMemo(
    () => [
      {
        key: "isMobile",
        label: t("Mobile OS"),
        status: checks.isMobile,
        value: checks.isMobile
          ? checks.isAndroid
            ? "Android"
            : checks.isiOS
              ? "iOS"
              : t("Unknown")
          : t("Mobile phone required"),
      },
      {
        key: "hasGeolocation",
        label: t("Geolocation Supported"),
        status: checks.hasGeolocation,
        value: checks.hasGeolocation ? t("Yes") : t("No"),
      },
      {
        key: "gpsEnabled",
        label: t("GPS Enabled"),
        status: checks.gpsEnabled,
        value: checks.gpsEnabled ? t("Yes") : t("No"),
        showHelp: hasChecked && !checks.gpsEnabled,
      },
      {
        key: "hasMotionSensors",
        label: t("Motion Sensors Available"),
        status: checks.hasMotionSensors,
        value: checks.hasMotionSensors ? t("Yes") : t("No"),
      },
      {
        key: "hasStorageAccess",
        label: t("Storage Access"),
        status: checks.hasStorageAccess,
        value: checks.hasStorageAccess ? t("Yes") : t("No"),
      },
      {
        key: "hasCookieAccess",
        label: t("Cookies Enabled"),
        status: checks.hasCookieAccess,
        value: checks.hasCookieAccess ? t("Yes") : t("No"),
      },
      {
        key: "onlineStatus",
        label: t("Online Status"),
        status: checks.onlineStatus,
        value: checks.onlineStatus ? t("Online") : t("Offline"),
      },
      {
        key: "hasCameraPermission",
        label: t("Camera Permission"),
        status: checks.hasCameraPermission,
        value: checks.hasCameraPermission ? t("Granted") : t("Denied"),
      },
    ],
    [checks, hasChecked, t]
  );

  const runChecks = async () => {
    clearMobileRequirementsPassed(game?.hash, team?.team_code);
    setIsChecking(true);
    setHasChecked(true);

    try {
      const result = await checkMobileRequirements();
      setChecks(result);

      if (canStartGame && (requirementsBypassed || areMobileRequirementsMet(result))) {
        markMobileRequirementsPassed(game?.hash, team?.team_code);
      }
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    runChecks();

    const handleOnline = () => {
      setChecks((prev) => ({ ...prev, onlineStatus: true }));
    };
    const handleOffline = () => {
      setChecks((prev) => ({ ...prev, onlineStatus: false }));
      clearMobileRequirementsPassed(game?.hash, team?.team_code);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasChecked && requirementsMet && canStartGame) {
      markMobileRequirementsPassed(game?.hash, team?.team_code);
    }
  }, [canStartGame, game?.hash, hasChecked, requirementsMet, team?.team_code]);

  const continueToGame = () => {
    if (!requirementsMet || !canStartGame) return;
    markMobileRequirementsPassed(game?.hash, team?.team_code);
    navigate(redirectTo, { replace: true });
  };

  return (
    <Layout>
      <div className="container py-4">
        <div className="mx-auto" style={{ maxWidth: "640px" }}>
          <h2 className="fw-bold mb-3">{t("Check mobile requirements")}</h2>
          <p className="text-muted mb-4">
            {t("mobileRequirementsIntro")}
          </p>

          {hasChecked && !isChecking && requirementsMet && (
            <Alert variant="success">{t("mobileRequirementsAllOk")}</Alert>
          )}

          {hasChecked && !isChecking && !requirementsMet && (
            <Alert variant="warning">
              {t("mobileRequirementsFailed")}
            </Alert>
          )}

          <div className="list-group mb-4">
            {requirementRows.map((row) => (
              <div key={row.key} className="list-group-item">
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">{row.label}</div>
                    <div className="small text-muted">{row.value}</div>
                    {row.showHelp && (
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 mt-1 small text-decoration-none"
                        onClick={() => setShowGpsHelp(true)}
                      >
                        ℹ️ {t("How to fix?")}
                      </Button>
                    )}
                  </div>
                  <span
                    className={`badge rounded-pill ${
                      row.status ? "text-bg-success" : "text-bg-danger"
                    }`}
                  >
                    {row.status ? "✓" : "!"}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="secondary"
            className="w-100 mb-3"
            onClick={runChecks}
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("Checking")}
              </>
            ) : (
              <>
                <span aria-hidden="true" className="me-1">↻</span>
                {t("Check again")}
              </>
            )}
          </Button>

          {canStartGame && (
            <Button
              type="button"
              variant="primary"
              className="w-100"
              onClick={continueToGame}
              disabled={!requirementsMet || isChecking}
            >
              {startGameButtonText}
            </Button>
          )}
        </div>
      </div>

      <Modal show={showGpsHelp} onHide={() => setShowGpsHelp(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("GPS location help")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{t("gpsHelpIntro")}</p>

          <h6 className="fw-bold">{t("For iPhone users")}</h6>
          <p className="mb-2">{t("To allow location access:")}</p>
          <ol>
            <li>{t("Open Settings")}</li>
            <li>{t("Open Privacy & Security")}</li>
            <li>{t("Open Location Services")}</li>
            <li>{t("Make sure Location Services is enabled")}</li>
            <li>{t("Find Safari Websites in the list")}</li>
            <li>{t("Change Never to While Using the App")}</li>
            <li>{t("Return to the game")}</li>
          </ol>

          <h6 className="fw-bold mt-4">{t("For Android users")}</h6>
          <ol>
            <li>{t("Open Settings")}</li>
            <li>{t("Apps")}</li>
            <li>{t("Chrome")}</li>
            <li>{t("Permissions")}</li>
            <li>{t("Location")}</li>
            <li>{t("Allow while using the app")}</li>
            <li>{t("Return to the game")}</li>
          </ol>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGpsHelp(false)}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default MobileRequirements;
